import { get, patch, post } from './httpClient';

export const COLLECTION_INQUIRY_MAIL_REGISTERED_MESSAGE = 'Quedó registrada; el mail no salió.';
export const COLLECTION_INQUIRY_RATE_LIMIT_MESSAGE = 'Ya mandaste varias consultas en esta lista. Probá más tarde.';
/** F3: mismo tope (20 / 24 h) pero por lote compartido. */
export const LOT_INQUIRY_RATE_LIMIT_MESSAGE = 'Ya mandaste varias consultas por este lote. Probá más tarde.';
/** F3: 503 `LOT_INQUIRY_NOT_ENABLED` (falta la SQL 088) o API anterior a F3. No se guardó nada. */
export const LOT_INQUIRY_NOT_ENABLED_MESSAGE = 'Todavía no se pueden enviar consultas desde un lote compartido.';
export const LOT_INQUIRY_ITEM_GONE_MESSAGE = 'Esa unidad ya no está en este lote compartido.';
export const LOT_INQUIRY_SHARE_GONE_MESSAGE = 'Este lote ya no está compartido.';
export const LIST_INQUIRY_ITEM_GONE_MESSAGE = 'Ese lote ya no está en la lista.';

/** 502 / 503 de mail: la consulta quedó guardada (A4). Éxito con aviso, sin reintento. */
const MAIL_SAVED_CODES = ['INQUIRY_MAIL_FAILED', 'MAIL_NOT_CONFIGURED'];

function readCode(response) {
  const code = response?.code ?? response?.data?.code;
  return typeof code === 'string' ? code.trim().toUpperCase() : '';
}

/**
 * ¿La API dejó la consulta guardada aunque respondió 502 / 503? `saved: true`
 * (M0) o, en respuestas viejas, un `inquiry_id` con 502 / 503.
 *
 * @param {object} response Envelope de POST /sendcollectioninquiry.
 * @returns {boolean}
 */
export function isCollectionInquirySavedDespiteMail(response) {
  const status = response?._httpStatus;
  if (status !== 502 && status !== 503) return false;
  const saved = response?.saved ?? response?.data?.saved;
  if (saved === true) return true;
  if (saved === false) return false;
  const code = readCode(response);
  if (code && !MAIL_SAVED_CODES.includes(code)) return false;
  // Respuesta anterior a M0: sin `saved`, el `inquiry_id` dice que se guardó.
  return Boolean(readInquiryId(response));
}

/**
 * F3: ¿la API dijo que la consulta por lote compartido no está habilitada?
 * **Solo** `code: LOT_INQUIRY_NOT_ENABLED` (503, zip nuevo sin SQL 088). Un
 * 400 (`VALIDATION_ERROR` de `share_token` / `kind` / …, `CONSENT_REQUIRED`
 * o la API vieja sin `code`) es un error común con su mensaje: tratarlo
 * como “no habilitado” escondía el error real detrás del aviso.
 *
 * @param {object} response
 * @param {string} kind
 * @returns {boolean}
 */
export function isLotInquiryNotEnabled(response, kind) {
  return kind === 'lot' && readCode(response) === 'LOT_INQUIRY_NOT_ENABLED';
}

function readApiError(response, fallback) {
  const text = response?.error || response?.message;
  const trimmed = typeof text === 'string' ? text.trim() : '';
  return trimmed || fallback;
}

function readInquiryId(response) {
  const id = response?.data?.inquiry_id ?? response?.inquiry_id;
  return typeof id === 'string' && id.trim() ? id.trim() : '';
}

function asNonNegativeInt(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return fallback;
  return Math.trunc(n);
}

function resolveCollectionQuery(query = {}) {
  const list_id = String(query.listId || query.list_id || '').trim();
  const project_id = String(query.projectId || query.project_id || query.id || '').trim();
  if (list_id && project_id) {
    return { error: { data: [], total: 0, has_more: false, unseen_total: 0, by_item: {}, _httpOk: false, _httpStatus: 400 } };
  }
  if (!list_id && !project_id) {
    return { error: { data: [], total: 0, has_more: false, unseen_total: 0, by_item: {}, _httpOk: false, _httpStatus: null } };
  }
  return { list_id, project_id };
}

/** SQL 098: 503 sin nada guardado → se puede reintentar. */
export const INQUIRY_LEAD_UNAVAILABLE_MESSAGE = 'Hubo un problema, reintentá.';
/** SQL 098: dueño / editor (`manage`) con su propio email. `field: "email"`. */
export const INQUIRY_OWNER_EMAIL_MESSAGE = 'No podés usar tu propio email; cargá el del cliente.';
export const INQUIRY_CONTACT_REQUIRED_MESSAGE = 'Cargá un email o un teléfono de contacto.';
export const INQUIRY_ITEM_REQUIRED_MESSAGE = 'Abrí el lote o la unidad por la que querés consultar.';
/** Códigos que señalan el campo email del form. */
export const INQUIRY_EMAIL_FIELD_CODES = ['INQUIRY_OWNER_EMAIL', 'INQUIRY_CONTACT_REQUIRED'];

/**
 * Mensaje de UI según el status HTTP de POST /sendcollectioninquiry.
 *
 * Decide por `code` (M0) cuando viene; sin `code`, por status como antes.
 *
 * @param {number|null} status
 * @param {string} [apiMessage]
 * @param {{ inquiryId?: string, code?: string, kind?: string }} [options]
 * @returns {string}
 */
export function getCollectionInquiryErrorMessage(status, apiMessage = '', options = {}) {
  const fromApi = String(apiMessage || '').trim();
  const inquiryId = String(options.inquiryId || '').trim();
  const code = String(options.code || '').trim().toUpperCase();
  const isLot = options.kind === 'lot';
  if (status === 401 || code === 'AUTH_REQUIRED') return 'Iniciá sesión para enviar la consulta.';
  if (code === 'LOT_INQUIRY_NOT_ENABLED') return LOT_INQUIRY_NOT_ENABLED_MESSAGE;
  // SQL 098: consulta y lead van juntos; si el lead no se pudo, no se guardó nada.
  if (code === 'INQUIRY_LEAD_UNAVAILABLE') return INQUIRY_LEAD_UNAVAILABLE_MESSAGE;
  if (code === 'INQUIRY_OWNER_EMAIL') return INQUIRY_OWNER_EMAIL_MESSAGE;
  if (code === 'INQUIRY_CONTACT_REQUIRED') return INQUIRY_CONTACT_REQUIRED_MESSAGE;
  if (code === 'INQUIRY_ITEM_REQUIRED') return INQUIRY_ITEM_REQUIRED_MESSAGE;
  if (code === 'ITEM_NOT_IN_SHARE') return LOT_INQUIRY_ITEM_GONE_MESSAGE;
  if (code === 'ITEM_NOT_IN_LIST') return LIST_INQUIRY_ITEM_GONE_MESSAGE;
  if (code === 'ITEM_NOT_IN_SHOWROOM') return fromApi || LIST_INQUIRY_ITEM_GONE_MESSAGE;
  if (isLot && code === 'COLLECTION_NOT_FOUND') return LOT_INQUIRY_SHARE_GONE_MESSAGE;
  if (isLot && code === 'COLLECTION_FORBIDDEN') return 'No tenés permiso para consultar por este lote.';
  if (status === 429 || code === 'INQUIRY_RATE_LIMIT') {
    return isLot ? LOT_INQUIRY_RATE_LIMIT_MESSAGE : COLLECTION_INQUIRY_RATE_LIMIT_MESSAGE;
  }
  if (status === 403 || status === 404) return fromApi || 'Esta lista no existe.';
  if (status === 409) {
    return fromApi || 'El dueño no tiene email de contacto. Pedile que cargue un mail en su identidad, o que entre una vez a la app.';
  }
  if (status === 502 || status === 503) {
    if (inquiryId) return fromApi || COLLECTION_INQUIRY_MAIL_REGISTERED_MESSAGE;
    return fromApi || 'No se pudo enviar la consulta. Intentá de nuevo.';
  }
  // API anterior a F3: 400 “kind must be showroom or list”, sin `code`.
  if (isLot && status === 400 && !code) return LOT_INQUIRY_NOT_ENABLED_MESSAGE;
  if (status === 400) return fromApi || 'Revisá los datos del formulario.';
  return fromApi || 'No se pudo enviar la consulta. Intentá de nuevo.';
}

/**
 * Visitante o ajeno: 404. No es un error de red; no hay bandeja.
 *
 * @param {number|null} status
 * @returns {boolean}
 */
export function isCollectionInquiryInboxUnavailable(status) {
  return status === 404;
}

function readItemCounts(value) {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const n = Math.max(0, Math.trunc(value));
    return { unseen: n, total: n };
  }
  if (typeof value !== 'object') return null;
  const unseen = asNonNegativeInt(value.unseen ?? value.unseen_total, 0);
  const total = asNonNegativeInt(value.total ?? value.count, unseen);
  return { unseen, total };
}

/**
 * `stats.by_item`: mapa por id (CAD: `parcel_id`) o lista de filas.
 * Si el id no está, ese lote no tuvo consultas.
 *
 * @param {object|array|null|undefined} raw
 * @returns {Record<string, { unseen: number, total: number }>}
 */
export function readCollectionInquiryByItem(raw) {
  const map = {};
  if (Array.isArray(raw)) {
    raw.forEach((row) => {
      const id = String(row?.parcel_id || row?.lot_id || row?.user_lot_id || row?.id || '').trim();
      const counts = readItemCounts(row);
      if (id && counts) map[id] = counts;
    });
    return map;
  }
  if (!raw || typeof raw !== 'object') return map;
  Object.entries(raw).forEach(([id, value]) => {
    const key = String(id || '').trim();
    const counts = readItemCounts(value);
    if (key && counts) map[key] = counts;
  });
  return map;
}

/**
 * Stats del GET de bandeja. Lista: `stats.total` + `stats.unseen_total`.
 * Lote: `stats.by_item`. No viven en `getcadproject`.
 *
 * @param {object} response
 * @returns {{
 *   total: number,
 *   unseen_total: number,
 *   by_item: Record<string, { unseen: number, total: number }>,
 * }}
 */
export function readCollectionInquiryStats(response) {
  const stats = response?.stats && typeof response.stats === 'object' ? response.stats : {};
  return {
    total: asNonNegativeInt(stats.total, 0),
    unseen_total: asNonNegativeInt(stats.unseen_total ?? response?.unseen_total, 0),
    by_item: readCollectionInquiryByItem(stats.by_item ?? response?.by_item),
  };
}


function asNullableTrimmedString(value) {
  if (value == null) return null;
  const s = String(value).trim();
  return s || null;
}

function asNullableNumber(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Fila de GET /getcollectioninquiries (API DEV d81c785: interest_stage /
 * offer_* / discard_reason aditivos, misma regla que getrecordleads).
 * Conserva el resto de campos; fuerza null cuando no hay valor usable.
 *
 * @param {object|null|undefined} row
 * @returns {object|null|undefined}
 */
export function normalizeCollectionInquiryRow(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    interest_stage: asNullableTrimmedString(row.interest_stage ?? row.interestStage),
    offer_amount: asNullableNumber(row.offer_amount ?? row.offerAmount),
    offer_currency: asNullableTrimmedString(row.offer_currency ?? row.offerCurrency),
    discard_reason: asNullableTrimmedString(row.discard_reason ?? row.discardReason),
  };
}

/**
 * Envelope del GET de bandeja.
 *
 * @param {object} response
 * @returns {{
 *   rows: object[],
 *   total: number,
 *   has_more: boolean,
 *   next_before: string,
 *   unseen_total: number,
 *   inquiry_total: number,
 *   by_item: Record<string, { unseen: number, total: number }>,
 * }}
 */
export function readCollectionInquiries(response) {
  const stats = readCollectionInquiryStats(response);
  const raw = Array.isArray(response?.data) ? response.data : [];
  return {
    rows: raw.map((row) => normalizeCollectionInquiryRow(row)),
    total: asNonNegativeInt(response?.total, 0),
    has_more: response?.has_more === true,
    next_before: String(response?.next_before || '').trim(),
    unseen_total: stats.unseen_total,
    inquiry_total: stats.total,
    by_item: stats.by_item,
  };
}

/**
 * POST /sendcollectioninquiry. JWT obligatorio. Mismo ACL que ver la colección.
 * Si SES falla después de insertar, viene `inquiry_id`: no reenviar el form.
 * 429 = tope 20 / usuario / lista / 24 h: un solo POST, sin loop.
 *
 * Lista CAD: `{ kind: 'showroom', project_id, parcel_id, name, email, phone?, message }`.
 * `project_id` es obligatorio. No mandar el UUID del showroom en `id`
 * (alias de parcel).
 * Lista: `{ kind: 'list', share_token?, list_id?, lot_id?, user_lot_id?, ... }`.
 * F3 lote compartido: `{ kind: 'lot', share_token, user_lot_id?, ... }`.
 *
 * 502 / 503 con `saved: true` (`INQUIRY_MAIL_FAILED` / `MAIL_NOT_CONFIGURED`)
 * → `registered`: éxito con aviso, **no** reenviar. `notEnabled` (solo
 * `kind: 'lot'`): la API todavía no acepta consultas por lote compartido.
 *
 * @param {object} body
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{
 *   ok: boolean,
 *   sent: boolean,
 *   registered?: boolean,
 *   rateLimited?: boolean,
 *   notEnabled?: boolean,
 *   code?: string,
 *   inquiry_id?: string,
 *   message?: string,
 *   _httpStatus: number|null,
 * }>}
 */
export async function sendCollectionInquiry(body, { signal } = {}) {
  try {
    const response = await post('/sendcollectioninquiry', {
      body,
      signal,
      retryOn429: 0,
    });
    const inquiryId = readInquiryId(response);
    if (response._httpOk && response.data?.sent === true) {
      // SQL 098: `lead_linked` / `lead_skip_reason` deprecados (siempre hay lead); no se leen.
      return {
        ok: true,
        sent: true,
        mailSent: response.data.mail_sent !== false,
        inquiry_id: inquiryId || undefined,
        message: response.message || 'Consulta enviada.',
        _httpStatus: response._httpStatus,
      };
    }
    const code = readCode(response);
    const kind = body?.kind;
    const registered = isCollectionInquirySavedDespiteMail(response);
    const notEnabled = isLotInquiryNotEnabled(response, kind);
    return {
      ok: false,
      sent: false,
      registered,
      rateLimited: response._httpStatus === 429,
      notEnabled,
      code: code || undefined,
      field: typeof response.field === 'string' ? response.field : undefined,
      inquiry_id: inquiryId || undefined,
      message: notEnabled
        ? LOT_INQUIRY_NOT_ENABLED_MESSAGE
        : getCollectionInquiryErrorMessage(
          response._httpStatus,
          readApiError(response, ''),
          { inquiryId: registered ? (inquiryId || 'saved') : '', code, kind },
        ),
      _httpStatus: response._httpStatus,
    };
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    console.error('Collection inquiry: POST /sendcollectioninquiry falló', error);
    return {
      ok: false,
      sent: false,
      message: 'No se pudo enviar la consulta. Intentá de nuevo.',
      _httpStatus: null,
    };
  }
}

/**
 * GET /getcollectioninquiries?list_id= o ?project_id=.
 * Solo dueño, editor o staff de esa colección. Visitante → 404.
 *
 * @param {{
 *   listId?: string,
 *   list_id?: string,
 *   projectId?: string,
 *   project_id?: string,
 *   id?: string,
 *   limit?: number,
 *   before?: string,
 *   unseen?: boolean,
 * }} query
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function getCollectionInquiries(query = {}, { signal } = {}) {
  const resolved = resolveCollectionQuery(query);
  if (resolved.error) return resolved.error;
  const limitRaw = Number(query.limit);
  const limit = Number.isFinite(limitRaw)
    ? Math.min(50, Math.max(1, Math.trunc(limitRaw)))
    : undefined;
  const before = String(query.before || '').trim();
  const unseen = query.unseen === true || query.unseen === 1 || query.unseen === '1'
    || query.unseen === 'true' || query.unseen === 'yes';
  return get('/getcollectioninquiries', {
    params: {
      ...(resolved.list_id ? { list_id: resolved.list_id } : { project_id: resolved.project_id }),
      ...(limit ? { limit } : {}),
      ...(before ? { before } : {}),
      ...(unseen ? { unseen: '1' } : {}),
    },
    signal,
  });
}

/**
 * PATCH /updatecollectioninquiry `{ id, seen }`.
 * Solo dueño/editor/staff de esa colección. Ajeno → 404.
 *
 * @param {{ id: string, seen: boolean }} body
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function updateCollectionInquiry(body = {}, { signal } = {}) {
  const id = String(body.id || '').trim();
  if (!id) {
    return { _httpOk: false, _httpStatus: 400, error: 'Falta el id de la consulta.' };
  }
  return patch('/updatecollectioninquiry', {
    body: { id, seen: body.seen === true },
    signal,
  });
}
